import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

const IDLE_TIMEOUT = 6 * 60 * 60 * 1000;  // 6 hours
const WARN_BEFORE  = 5 * 60 * 1000;        // warn 5 minutes before logout

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

export function useIdleLogout() {
  const navigate = useNavigate();
  const { toast, dismiss } = useToast();
  const idleTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnToastId = useRef<string | undefined>(undefined);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("stockwise-user-cache");
    window.dispatchEvent(new CustomEvent("authStateChange", { detail: false }));
    navigate("/login?sessionExpired=true", { replace: true });
  }, [navigate]);

  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (idleTimer.current)  clearTimeout(idleTimer.current);
    if (warnTimer.current)  clearTimeout(warnTimer.current);

    // Dismiss any existing warning toast
    if (warnToastId.current) {
      dismiss(warnToastId.current);
      warnToastId.current = undefined;
    }

    // Set warning timer (fires 1 min before logout)
    warnTimer.current = setTimeout(() => {
      const { id } = toast({
        title: "Still there?",
        description: "You'll be logged out in 1 minute due to inactivity.",
        duration: WARN_BEFORE,
      });
      warnToastId.current = id;
    }, IDLE_TIMEOUT - WARN_BEFORE);

    // Set logout timer
    idleTimer.current = setTimeout(logout, IDLE_TIMEOUT);
  }, [logout, toast, dismiss]);

  useEffect(() => {
    // Only run when logged in
    if (!localStorage.getItem("token")) return;

    resetTimers();

    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, resetTimers, { passive: true })
    );

    return () => {
      if (idleTimer.current)  clearTimeout(idleTimer.current);
      if (warnTimer.current)  clearTimeout(warnTimer.current);
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, resetTimers)
      );
    };
  }, [resetTimers]);
}
